/**
 * PolicyGate — blokujący modal wymuszający akceptację dokumentów polityki.
 * Ported from school-transport-platform 2026-06-05.
 *
 * Mountowany w AppLayout — po zalogowaniu pobiera /legal/pending i jeśli
 * są niezaakceptowane dokumenty, renderuje modal blokujący resztę UI.
 *
 * Użytkownik musi: zaakceptować WSZYSTKIE dokumenty kolejno LUB odmówić
 * (co blokuje konto i wylogowuje).
 */
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import { ShieldAlert, Check, X, Loader2, FileText, Lock } from 'lucide-react';
import { api } from '@/shared/lib/api';
import { useAuthStore } from '@store/auth.store';

interface PendingDoc {
  type:     string;
  version:  string;
  title:    string;
  body:     string;
  isUpdate: boolean;
}

interface PendingResponse {
  locked:        boolean;
  lockedAt:      string | null;
  lockedReason:  string | null;
  pending:       PendingDoc[];
}

export function PolicyGate() {
  const { user, logout } = useAuthStore();
  const qc = useQueryClient();
  const [currentIdx, setCurrentIdx] = useState(0);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  const { data, isLoading } = useQuery<PendingResponse>({
    queryKey: ['legal', 'pending'],
    queryFn:  () => api.get('/legal/pending') as any,
    enabled:  !!user,
    refetchOnWindowFocus: false,
  });

  const acceptMut = useMutation({
    mutationFn: (doc: PendingDoc) => api.post('/legal/accept', {
      type: doc.type, version: doc.version, locale: 'pl',
    }) as any,
    onSuccess: () => {
      if (data && currentIdx < data.pending.length - 1) {
        setCurrentIdx(i => i + 1);
      } else {
        qc.invalidateQueries({ queryKey: ['legal', 'pending'] });
        setCurrentIdx(0);
        toast.success('Wszystkie dokumenty zaakceptowane');
      }
    },
    onError: () => toast.error('Nie udało się zapisać akceptacji'),
  });

  const declineMut = useMutation({
    mutationFn: () => api.post('/legal/decline', { reason: declineReason || null }) as any,
    onSuccess: () => {
      toast.error('Konto zostało zablokowane. Wylogowanie...');
      setTimeout(() => logout(), 1500);
    },
    onError: () => toast.error('Nie udało się odmówić'),
  });

  // Reset idx on data change
  useEffect(() => {
    if (data && currentIdx >= data.pending.length) setCurrentIdx(0);
  }, [data, currentIdx]);

  if (!user || isLoading || !data) return null;

  // Locked screen
  if (data.locked) {
    return (
      <div className="fixed inset-0 z-[10500] flex items-center justify-center px-4"
        style={{ background: 'rgba(15,23,42,0.85)', backdropFilter: 'blur(8px)' }}>
        <motion.div initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }}
          className="w-full max-w-md bg-card rounded-2xl p-8 shadow-2xl border border-border text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 bg-red-100">
            <Lock className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-foreground">Konto zablokowane</h2>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
            Twoje konto zostało zablokowane z powodu odmowy akceptacji dokumentów polityki systemu.
          </p>
          {data.lockedReason && (
            <p className="text-xs text-muted-foreground mt-3 italic">Powód: {data.lockedReason}</p>
          )}
          <p className="text-xs text-muted-foreground mt-4">
            Skontaktuj się z administratorem systemu, aby odblokować dostęp.
          </p>
          <button onClick={() => logout()}
            className="mt-6 px-4 py-2 rounded-xl text-sm font-bold text-white bg-slate-600 hover:bg-slate-700 transition-colors">
            Wyloguj się
          </button>
        </motion.div>
      </div>
    );
  }

  if (data.pending.length === 0) return null;

  const doc = data.pending[currentIdx];
  if (!doc) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="fixed inset-0 z-[10500] flex items-center justify-center px-4 py-8"
        style={{ background: 'rgba(15,23,42,0.85)', backdropFilter: 'blur(8px)' }}>
        <motion.div initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }}
          className="w-full max-w-3xl max-h-[90vh] bg-card rounded-2xl shadow-2xl border border-border flex flex-col overflow-hidden">

          {/* Hero */}
          <div className="relative overflow-hidden text-white"
            style={{ background: 'linear-gradient(135deg, #0d4a46 0%, #0d9488 60%, #14b8a6 100%)' }}>
            <div className="absolute -top-20 -right-12 w-44 h-44 rounded-full" style={{ background: 'rgba(255,255,255,0.10)' }} />
            <div className="relative px-6 pt-5 pb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.18)' }}>
                  <ShieldAlert className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-bold tracking-[0.18em] opacity-85">WYMAGANA AKCEPTACJA</p>
                  <h2 className="text-lg font-extrabold leading-tight mt-0.5">{doc.title}</h2>
                </div>
              </div>
              <div className="flex items-center gap-2 text-[11px] opacity-90 flex-wrap">
                <span>Dokument {currentIdx + 1} z {data.pending.length}</span>
                <span>·</span>
                <span className="font-mono">wersja {doc.version}</span>
                {doc.isUpdate && (
                  <>
                    <span>·</span>
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider"
                      style={{ background: 'rgba(255,255,255,0.22)' }}>
                      Zaktualizowane
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Body */}
          {!declining ? (
            <>
              <div className="flex-1 overflow-y-auto px-6 py-5 text-[13px] leading-relaxed text-foreground/90">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => <h1 className="text-[18px] font-extrabold text-foreground mt-1 mb-3">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-[15px] font-bold text-foreground mt-5 mb-2 border-b border-border/60 pb-1">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-[14px] font-bold text-foreground mt-4 mb-1.5">{children}</h3>,
                    h4: ({ children }) => <h4 className="text-[13px] font-semibold text-foreground mt-3 mb-1">{children}</h4>,
                    p:  ({ children }) => <p className="my-2.5">{children}</p>,
                    ul: ({ children }) => <ul className="list-disc pl-5 my-2.5 space-y-1">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal pl-5 my-2.5 space-y-1">{children}</ol>,
                    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                    a:  ({ children, href }) => <a href={href} target="_blank" rel="noreferrer" className="text-primary underline decoration-primary/30 hover:decoration-primary underline-offset-2">{children}</a>,
                    strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
                    em: ({ children }) => <em className="italic text-muted-foreground">{children}</em>,
                    hr: () => <hr className="my-4 border-border" />,
                    code: ({ children }) => <code className="bg-muted text-foreground px-1.5 py-0.5 rounded font-mono text-[12px]">{children}</code>,
                    blockquote: ({ children }) => <blockquote className="my-3 p-3 rounded-lg border-l-4 border-primary bg-primary/5 text-foreground/90"><div className="[&>p]:my-0">{children}</div></blockquote>,
                    table: ({ children }) => <div className="overflow-x-auto my-3 rounded-lg border border-border"><table className="w-full text-[12px] border-collapse">{children}</table></div>,
                    thead: ({ children }) => <thead className="bg-muted/50">{children}</thead>,
                    th: ({ children }) => <th className="text-left font-semibold text-foreground px-3 py-2 border-b border-border">{children}</th>,
                    td: ({ children }) => <td className="px-3 py-2 border-b border-border/60 align-top">{children}</td>,
                  }}
                >{doc.body}</ReactMarkdown>
              </div>

              <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-between gap-3 flex-wrap">
                <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" />
                  Akceptując wyrażasz zgodę na warunki dokumentu i zostaniesz zarejestrowany w dzienniku audytu.
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setDeclining(true)} disabled={acceptMut.isPending}
                    className="px-4 py-2 rounded-xl text-[12px] font-bold text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                    Odmów
                  </button>
                  <button onClick={() => acceptMut.mutate(doc)} disabled={acceptMut.isPending}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-[13px] font-extrabold text-white bg-primary hover:bg-primary/90 disabled:opacity-40 transition-colors">
                    {acceptMut.isPending
                      ? <><Loader2 className="w-4 h-4 animate-spin" />Zapisywanie…</>
                      : <><Check className="w-4 h-4" />
                          {currentIdx < data.pending.length - 1 ? 'Akceptuję i dalej' : 'Akceptuję'}
                        </>}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 px-6 py-6 flex flex-col gap-4">
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-red-900">Czy na pewno chcesz odmówić?</p>
                  <p className="text-[12px] text-red-700 mt-1 leading-relaxed">
                    Odmowa zablokuje Twoje konto. Aby uzyskać ponowny dostęp do systemu, musisz skontaktować się z administratorem.
                  </p>
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">
                  Powód odmowy (opcjonalnie)
                </label>
                <textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)}
                  maxLength={500} rows={3}
                  placeholder="Możesz podać powód, dla którego nie akceptujesz dokumentu…"
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-border bg-muted/40 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 resize-none" />
              </div>

              <div className="mt-auto pt-4 border-t border-border flex items-center justify-end gap-2">
                <button onClick={() => { setDeclining(false); setDeclineReason(''); }}
                  disabled={declineMut.isPending}
                  className="px-4 py-2 rounded-xl text-[12px] font-bold text-muted-foreground hover:bg-muted transition-colors">
                  Wróć do dokumentu
                </button>
                <button onClick={() => declineMut.mutate()} disabled={declineMut.isPending}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-[13px] font-extrabold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 transition-colors">
                  {declineMut.isPending
                    ? <><Loader2 className="w-4 h-4 animate-spin" />Wysyłanie…</>
                    : <><X className="w-4 h-4" />Potwierdź odmowę i wyloguj</>}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
